/**
 * Shared job cards grid + empty state for Gigs and Saved Jobs tabs
 */

import React from 'react';
import {
  Briefcase,
  Calendar,
  MapPin,
  Clock,
  Users,
  Heart,
  Share2,
  ChevronRight,
  Star,
  AlertCircle,
  Pencil,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../../utils/cn';
import type { JobCardData } from '../../../types/jobs';
import type { EventPricingApiItem } from '../../../services/artistService';
import { useArtistDetailsStore } from '../../../stores/artistDetailsStore';
import { isSingerOrLiveBandCategory } from '../../../data/membershipPlansData';
import { toEventTypeDisplayLabel } from '../../../utils/eventTypeDisplay';
import { toJobDescriptionDisplayText } from '../../../utils/jobDescriptionDisplay';

export type JobsListTabKind = 'gigs' | 'saved';

export interface JobCardsListProps {
  jobs: JobCardData[];
  tabKind: JobsListTabKind;
  /** Only relevant for `tabKind="gigs"` when filtering Standard/Express/All. */
  gigsActiveType?: 'standard' | 'express' | 'all';
  hideCoinsBadge?: boolean;
  onToggleLike: (jobId: string) => void;
  likeUpdatingIds: Set<string>;
  onShare: (job: JobCardData) => void;
  onApply: (job: JobCardData) => void;
  onOpenAddPricingForJob: (job: JobCardData) => void;
  canApplyToJob: (eventPricingData: EventPricingApiItem[], jobEventType: string) => boolean;
  eventPricingData: EventPricingApiItem[];
}

export const JobCardsList: React.FC<JobCardsListProps> = ({
  jobs,
  tabKind,
  gigsActiveType,
  hideCoinsBadge = false,
  onToggleLike,
  likeUpdatingIds,
  onShare,
  onApply,
  onOpenAddPricingForJob,
  canApplyToJob,
  eventPricingData,
}) => {
  const navigate = useNavigate();
  const ebEnabledStatus = useArtistDetailsStore((s) => s.data?.eb_enabled);
  const ebTncAccepted = useArtistDetailsStore((s) => s.data?.eb_tnc);
  const artistCategoryName = useArtistDetailsStore((s) => s.data?.artist_category?.name);

  const isExpressJob = (job: JobCardData): boolean =>
    job.badge === 'Express' || (job.type ?? '').trim().toLowerCase() === 'express';

  const showExpressUnderReviewBanner = ebEnabledStatus === 2 && jobs.some(isExpressJob);

  if (jobs.length === 0) {
    const isGigsExpressFilter = tabKind === 'gigs' && gigsActiveType === 'express';
    const showExpressConsentRequiredEmptyState = isGigsExpressFilter && ebTncAccepted === false;
    const showExpressUnderReviewEmptyState = isGigsExpressFilter && ebEnabledStatus === 2;
    const noGigsEmptyTitle = isSingerOrLiveBandCategory(artistCategoryName)
      ? 'No jobs in this location'
      : 'No jobs in this Category';

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="rounded-2xl border border-[#3A3A3C] bg-[#1C1C1E] p-12 text-center"
      >
        {showExpressConsentRequiredEmptyState ? (
          <>
            <AlertCircle className="h-14 w-14 text-[#F33363] mx-auto mb-4 opacity-80" />
            <p className="text-lg font-medium text-white mb-1">
              Express Booking is not enabled yet
            </p>
            <p className="text-sm text-[#8E8E93] mb-5">
              Please complete Express Booking consent form to start seeing Express jobs.
            </p>
            <button
              type="button"
              onClick={() => navigate('/express-booking-consent-form')}
              className="mx-auto inline-flex items-center justify-center rounded-2xl bg-[#F33363] px-5 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-[#FF5E8E] active:scale-[0.99] transition-all"
            >
              First, enable Express Booking
            </button>
          </>
        ) : showExpressUnderReviewEmptyState ? (
          <>
            <AlertCircle className="h-14 w-14 text-[#F33363] mx-auto mb-4 opacity-80" />
            <p className="text-lg font-medium text-white mb-1">
              Your Express Booking is Currently Under Review
            </p>
            <p className="text-sm text-[#8E8E93]">
              Please wait while we review your Express Booking request.
            </p>
          </>
        ) : (
          <>
            {tabKind === 'saved' ? (
              <Heart className="h-14 w-14 text-[#8E8E93] mx-auto mb-4 opacity-50" />
            ) : (
              <Briefcase className="h-14 w-14 text-[#8E8E93] mx-auto mb-4 opacity-50" />
            )}
            <p className="text-lg font-medium text-white mb-1">
              {tabKind === 'saved' ? 'No saved jobs' : noGigsEmptyTitle}
            </p>
            <p className="text-sm text-[#8E8E93]">
              {tabKind === 'saved'
                ? 'Jobs you save will appear here for quick access.'
                : 'Check back later for new opportunities.'}
            </p>
          </>
        )}
      </motion.div>
    );
  }

  return (
    <div className="space-y-4">
      {showExpressUnderReviewBanner && (
        <div className="rounded-2xl border border-[#F33363]/25 bg-[#4B0017]/25 px-4 py-3">
          <p className="text-sm font-semibold text-white text-center">
            Your Express Booking is Currently Under Review
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4 space-y-3 lg:space-y-4">
      {jobs.map((job, index) => (
        <motion.article
          key={job.id}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.05 }}
          className="group relative bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] p-4 lg:px-6 lg:py-4 hover:border-[#F33363]/30 hover:bg-[#212123] transition-all duration-300"
        >
          {/* Top Utility Bar (Badges & Actions) */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              {job.badge && (
                <span className={cn(
                  "px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider border whitespace-nowrap",
                  job.badge === 'AI Recommended'
                    ? "bg-purple-500/10 text-purple-400 border-purple-500/20"
                    : "bg-orange-500/10 text-orange-400 border-orange-500/20"
                )}>
                  {job.badge === 'AI Recommended' ? '✨ Recommendation' : '⚡ Express'}
                </span>
              )}
              <span className="px-2 py-0.5 rounded-md bg-[#2C2C2E]/50 border border-[#3A3A3C] text-[9px] font-bold text-[#8E8E93] uppercase tracking-wider">
                {toEventTypeDisplayLabel(job.category)}
              </span>
              {isExpressJob(job) && job.venue_type && (
                <>
                  <span className="px-2 py-0.5 rounded-md bg-[#2C2C2E]/50 border border-[#3A3A3C] text-[9px] font-bold text-[#8E8E93] uppercase tracking-wider whitespace-nowrap">
                    {job.venue_type === 'Indoor' ? 'With Sound Setup' : 'Outdoor'}
                  </span>
                  {job.venue_type === 'Indoor' && (
                    <span className="px-2 py-0.5 rounded-md bg-[#2C2C2E]/50 border border-[#3A3A3C] text-[9px] font-bold text-[#8E8E93] uppercase tracking-wider whitespace-nowrap">
                      Indoor
                    </span>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => onToggleLike(job.id)}
                disabled={likeUpdatingIds.has(job.id)}
                className={cn(
                  "p-1.5 rounded-lg bg-[#2C2C2E] border border-[#3A3A3C] transition-all hover:scale-105",
                  likeUpdatingIds.has(job.id)
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:border-[#F33363]/50"
                )}
                aria-label={
                  tabKind === 'gigs'
                    ? job.isSaved ? 'Unlike' : 'Like'
                    : job.isSaved ? 'Unsave' : 'Save'
                }
              >
                <Heart
                  className={cn(
                    'h-3.5 w-3.5',
                    job.isSaved ? 'fill-[#F33363] text-[#F33363]' : 'text-[#8E8E93]'
                  )}
                />
              </button>
              <button
                onClick={() => onShare(job)}
                className="p-1.5 rounded-lg bg-[#2C2C2E] border border-[#3A3A3C] hover:border-[#F33363]/50 transition-all hover:scale-105"
                aria-label="Share"
              >
                <Share2 className="h-3.5 w-3.5 text-[#8E8E93]" />
              </button>
            </div>
          </div>

          <div className="flex flex-col xl:flex-row xl:items-center gap-4 lg:gap-4 xl:gap-8 w-full">
            <div className="flex flex-col flex-1 min-w-0">
              <h3 className="text-base lg:text-lg font-bold text-white leading-tight group-hover:text-[#F33363] transition-colors pr-6 lg:pr-0 whitespace-normal break-words">
                {job.title ?? `${job.eventType} ${job.category} in ${job.city}`}
              </h3>
              <div className="flex items-center gap-2 text-sm text-[#8E8E93]">
                <MapPin className="h-4 w-4 text-[#F33363] flex-shrink-0" />
                {isExpressJob(job) && job.distance_km != null ? (
                  <div className="flex flex-wrap items-start gap-2 min-w-0">
                    <span className="min-w-0 flex-1 whitespace-normal break-words leading-snug">
                      {job.distance_km.toFixed(2)} km from your Entered location
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate('/express-booking')}
                      className="flex-shrink-0 p-1 text-[#8E8E93] hover:text-[#F33363] transition-colors"
                      aria-label="Edit pincode"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <span className="truncate">{job.location}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 md:gap-6 lg:gap-4 xl:gap-8 py-4 lg:py-0 border-y lg:border-y-0 lg:border-x border-[#3A3A3C]/50 lg:px-4 xl:px-0 flex-shrink-0 flex-wrap sm:flex-nowrap">
              <div className="flex flex-col gap-0.5 min-w-[80px] xl:min-w-[100px]">
                <span className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-widest">Date</span>
                <div className="flex items-center gap-1.5 text-xs xl:text-sm text-white font-medium">
                  <Calendar className="h-3.5 w-3.5 text-[#8E8E93] flex-shrink-0" />
                  {job.date}
                </div>
              </div>
              <div className="hidden sm:block w-px h-6 bg-[#3A3A3C]/30 lg:hidden" />
              <div className="flex flex-col gap-0.5 min-w-[70px] xl:min-w-[90px]">
                <span className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-widest">Duration</span>
                <div className="flex items-center gap-1.5 text-xs xl:text-sm text-white font-medium">
                  <Clock className="h-3.5 w-3.5 text-[#8E8E93] flex-shrink-0" />
                  {job.durationMinutes}m
                </div>
              </div>
              <div className="hidden sm:block w-px h-6 bg-[#3A3A3C]/30 lg:hidden" />
              <div className="flex flex-col gap-0.5 min-w-[70px] xl:min-w-[80px]">
                <span className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-widest">Audience</span>
                <div className="flex items-center gap-1.5 text-xs xl:text-sm text-white font-medium">
                  <Users className="h-3.5 w-3.5 text-[#8E8E93] flex-shrink-0" />
                  {job.attendees}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between lg:justify-end gap-3 lg:gap-6 lg:min-w-[180px] xl:min-w-[220px] flex-shrink-0">
              <div className="text-left lg:text-right">
                <p className="text-lg lg:text-xl font-black text-white">
                  {job.budgetDisplay ?? `₹${job.amount.toLocaleString()}`}
                </p>
                <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-widest">Client Budget</p>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                {(() => {
                  const canApply = canApplyToJob(eventPricingData, job.eventType ?? job.eventTag ?? '');
                  const isRecommendedJob = (job.type ?? '').toLowerCase() === 'recommended';
                  const hidePricingGates = isExpressJob(job);
                  const canTakeAction = hidePricingGates ? job.canClaim !== false : canApply;
                  return (
                    <>
                      {!hidePricingGates && !canApply && (
                        <button
                          type="button"
                          onClick={() => onOpenAddPricingForJob(job)}
                          className="text-[10px] font-semibold text-amber-400 hover:text-amber-300 underline underline-offset-1"
                        >
                          Add pricing to apply
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onApply(job)}
                        aria-disabled={!canTakeAction}
                        className={cn(
                          'h-9 px-4 lg:px-5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap',
                          canTakeAction
                            ? 'bg-[#F33363] text-white hover:bg-[#FF5E8E] active:scale-95'
                            : 'bg-[#F33363]/15 text-[#F33363] border border-[#F33363]/40 hover:bg-[#F33363]/20 hover:border-[#F33363]/60'
                        )}
                      >
                        {hidePricingGates
                          ? 'Claim'
                          : isRecommendedJob
                            ? 'Apply for Free'
                            : 'Apply'}
                        <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

          {!isExpressJob(job) && !canApplyToJob(eventPricingData, job.eventType ?? job.eventTag ?? '') && (
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 text-amber-500" />
              <p className="text-xs text-amber-200">
                Add your pricing for <span className="font-semibold text-white">{job.eventType ?? job.eventTag}</span> in Event Pricing to apply.
              </p>
              <button
                type="button"
                onClick={() => onOpenAddPricingForJob(job)}
                className="ml-auto flex-shrink-0 rounded-lg bg-amber-500/20 px-2.5 py-1 text-[10px] font-bold text-amber-300 hover:bg-amber-500/30"
              >
                Add pricing
              </button>
            </div>
          )}

          <div className="mt-3 pt-2 border-t border-[#3A3A3C]/30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            {job.description ? (
              <button
                type="button"
                onClick={() => navigate(`/jobs/${job.id}`)}
                className="text-left text-xs text-[#8E8E93] flex-1 min-w-0 flex items-start gap-1.5 group/desc hover:text-[#F33363] transition-colors"
              >
                <span className="line-clamp-2 flex-1">{toJobDescriptionDisplayText(job.description)}</span>
                <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-[#8E8E93] group-hover/desc:text-[#F33363] transition-colors" />
              </button>
            ) : (
              <span className="flex-1" />
            )}
            {!hideCoinsBadge && (job.type ?? '').trim().toLowerCase() !== 'express' && (
              <div className="flex items-center gap-1 px-2 py-0.5 w-fit rounded-md bg-[#F33363]/5 border border-[#F33363]/10 flex-shrink-0">
                <Star className="h-2.5 w-2.5 text-[#F33363] fill-[#F33363]" />
                <span className="text-[9px] font-bold text-[#F33363] uppercase tracking-tighter">
                  {job.coinsToApply} StarCoins required
                </span>
              </div>
            )}
          </div>
        </motion.article>
      ))}
      </div>
    </div>
  );
};
